import gsap from "gsap";
import { CustomEase } from "gsap/all";
import DefaultBlock from "/scripts/default/block";
import { watchPreferredMotion } from "/utils/helpers/watchPreferredMotion";

// Register Plugins
gsap.registerPlugin(CustomEase);

export default class ScrollHero extends DefaultBlock {
	// Define variables
	init() {
		const selector = gsap.utils.selector(this.block);

		const DOM = {
			block: this.block,
			track: selector(".scroll-hero__flair-track"),
			scrollTubes: selector(
				"#hero-scroll-tube-1, #hero-scroll-tube-2, #hero-scroll-tube-3"
			),
			flowers: selector("#hero-flower-1"),
			shortTubes: selector("#hero-short-tube-1, #hero-short-tube-2"),
			longTube: selector("#hero-long-tube-1"),
			button: selector("#hero-button"),
			buttonInner: selector("#hero-button div"),
			buttonSvg: selector("#hero-button svg"),
			trackShapes: selector(
				"#hero-scroll-tube-1, #hero-scroll-tube-2, #hero-short-tube-1, #hero-long-tube-1, #hero-scroll-tube-3, #hero-short-tube-2"
			),
		};
		this.DOM = DOM;

		this.defaults = {
			ease: "power2.out",
			duration: 0.6,
		};

		this.tl = this.createTimeline();
	}

	drawCircleTube(tube, duration, from = "end") {
		const circles = tube.querySelectorAll("circle");

		gsap.set(circles, { opacity: 0 });

		return gsap.to(circles, {
			opacity: 1,
			duration: 0,
			stagger: {
				amount: duration,
				from: from,
			},
		});
	}

	scrollTube1() {
		return this.drawCircleTube(this.DOM.scrollTubes[0], 2);
	}

	scrollTube2() {
		return this.drawCircleTube(this.DOM.scrollTubes[1], 2, "start");
	}

	scrollTube3() {
		return this.drawCircleTube(this.DOM.scrollTubes[2], 2);
	}

	flower1() {
		const tl = gsap.timeline({
			defaults: this.defaults,
		});

		tl.fromTo(
			this.DOM.flowers[0],
			{
				scale: 0,
			},
			{
				scale: 0.33,
			}
		);

		tl.to(
			this.DOM.flowers[0],
			{
				scale: 0.66,
			},
			"+=.6"
		);

		tl.to(
			this.DOM.flowers[0],
			{
				scale: 1,
			},
			"+=.6"
		);

		return tl;
	}

	button() {
		const flowerSvg = this.DOM.flowers[0].querySelector("svg");

		const tl = gsap.timeline({
			id: "scroll-hero-button",
			repeat: -1,
		});

		const durationPerSquare = 0.4;

		const cornerEase1 = "power1.out";
		const cornerEase2 = "power1.in";

		const duration = (squaresMoved) => {
			return durationPerSquare * squaresMoved;
		};

		gsap.to(this.DOM.buttonInner, {
			rotationZ: 360,
			duration: 1,
			repeat: -1,
			ease: "none",
		});

		tl.set(this.DOM.button, {
			xPercent: -900,
			autoAlpha: 1,
		});
		tl.to(
			this.DOM.button,
			{
				keyframes: [
					{ xPercent: -800, ease: cornerEase2, duration: duration(2) },
					{ xPercent: -500, duration: duration(3) },
					{ xPercent: -400, ease: cornerEase1, duration: duration(2) },
					{ xPercent: -300, duration: duration(1) },
					{ xPercent: -300, duration: 0.4 },
					{ xPercent: -200, ...this.defaults },
					{ xPercent: -100, ease: cornerEase2, duration: duration(2) },
					{ xPercent: 0, duration: duration(1) },
					{ xPercent: 100, ease: cornerEase1, duration: duration(2) },
					{ xPercent: 400, duration: duration(3) },
					{ xPercent: 500, ease: cornerEase2, duration: duration(2) },
					{ xPercent: 600, duration: duration(1) },
					{ xPercent: 700, ease: cornerEase1, duration: duration(2) },
					{ xPercent: 800, duration: duration(1) },
				],
				ease: "none",
			},
			0
		);
		tl.to(
			this.DOM.button,
			{
				keyframes: [
					{ yPercent: -100, ease: cornerEase1, duration: duration(2) },
					{ yPercent: -100, duration: duration(3) },
					{ yPercent: 0, ease: cornerEase2, duration: duration(2) },
					{ yPercent: 0, duration: duration(1) },
					{ yPercent: 0, duration: 0.4 },
					{ yPercent: 0, ...this.defaults },
					{ yPercent: -100, ease: cornerEase1, duration: duration(2) },
					{ yPercent: -100, duration: duration(1) },
					{ yPercent: 0, ease: cornerEase2, duration: duration(2) },
					{ yPercent: 0, duration: duration(3) },
					{ yPercent: -100, ease: cornerEase1, duration: duration(2) },
					{ yPercent: -100, duration: duration(1) },
					{ yPercent: 0, ease: cornerEase2, duration: duration(2) },
					{ yPercent: 0, duration: duration(1) },
				],
				ease: "none",
			},
			0
		);
		tl.to(
			flowerSvg,
			{
				keyframes: [
					{ rotationZ: 90, ...this.defaults },
					{ rotationZ: 180, ...this.defaults, delay: 0.6 },
					{ rotationZ: 270, ...this.defaults, delay: 0.6 },
					{ rotationZ: 360, ...this.defaults, delay: 0.6 },
					{ rotationZ: 450, ...this.defaults, delay: 0.6 },
					{ rotationZ: 540, ...this.defaults, delay: 0.6 },
					{ rotationZ: 630, ...this.defaults, delay: 0.6 },
					{ rotationZ: 720, ...this.defaults, delay: 0.6 },
				],
			},
			0
		);

		return tl;
	}

	createTimeline() {
		const tl = gsap.timeline({
			id: "scroll-hero",
			defaults: this.defaults,
		});

		tl.set(this.DOM.track, { autoAlpha: 1 });

		const mm = gsap.matchMedia();

		watchPreferredMotion(() => {
			/* Animate */
			tl.from(this.DOM.trackShapes, {
				autoAlpha: 0,
				y: 75,
				x: 175,
				scale: 0.4,
				stagger: 0.1,
			});

			this.flower1();
			this.button();
		});

		return tl;
	}
}
